/**
 * Stands the theme's bottom surfaces down while the consent banner is up.
 *
 * Shopify's cookie banner is fixed at z-index 2000000, and on a phone it is not
 * a thin strip: measured on a Pixel 7 it is 424px tall in an 839px viewport,
 * covering the bottom half. It sat directly on the sticky purchase bar, so a
 * European visitor scrolled a product page, saw "Add to cart" appear, and could
 * not press it.
 *
 * The first attempt offset the bar by the banner's height. That is right for a
 * thin bar and wrong for this one: lifted 424px, the purchase bar would float
 * in the middle of the page on top of the product photography.
 *
 * So the bar stands down instead. It is a convenience, not the only way to buy:
 * the main Add to cart button is still there in the page, and the bar returns
 * the moment the visitor answers the banner, which is one tap. Nothing is taken
 * away that is not immediately given back.
 *
 * `--consent-height` is published as well, for anything that does want to sit
 * just above the banner.
 */

const ROOT = document.documentElement;

/**
 * The banner, not the stylesheet that shares its prefix.
 *
 * `#shopify-pc__banner` also matches `<style id="shopify-pc__banner__styles">`,
 * which comes first in the document and measures zero. Selecting on the fixed
 * position is what tells the panel from its stylesheet.
 */
function findBanner() {
  const candidates = document.querySelectorAll('[id^="shopify-pc__banner"], [class*="shopify-pc__banner__dialog"]');
  for (const node of candidates) {
    if (node.tagName === 'STYLE' || node.tagName === 'SCRIPT') continue;
    const style = getComputedStyle(node);
    if (style.position !== 'fixed') continue;
    if (style.display === 'none' || style.visibility === 'hidden') continue;
    if (node.getBoundingClientRect().height > 0) return node;
  }
  return null;
}

function apply() {
  const banner = findBanner();
  if (!banner) {
    ROOT.removeAttribute('data-consent-visible');
    ROOT.style.removeProperty('--consent-height');
    return;
  }
  ROOT.setAttribute('data-consent-visible', '');
  ROOT.style.setProperty('--consent-height', `${Math.round(banner.getBoundingClientRect().height)}px`);
}

let watched = null;
const sizes = new ResizeObserver(() => apply());

function attach() {
  const banner = findBanner();
  if (banner !== watched) {
    if (watched) sizes.unobserve(watched);
    watched = banner;
    if (banner) sizes.observe(banner);
  }
  apply();
}

// Shopify injects the banner after the theme and removes it once answered, so
// the body is watched rather than polled.
new MutationObserver(attach).observe(document.body, { childList: true, subtree: true });

attach();
document.addEventListener('visitorConsentCollected', () => setTimeout(attach, 80));
